import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Zap,
  Play,
  Square,
  RefreshCw,
  Filter,
  Layers,
  Clock,
  ChevronDown,
  ChevronRight,
  BarChart2,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Settings,
  Code,
  ExternalLink,
  Send
} from 'lucide-react';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import {
  dynamicAnalysisApi,
  type DynamicJobEntry,
  type DynamicEntryResult,
  getEntryStatusVariant,
  getScheduleLabel
} from '../../services/dynamic-analysis-api';
import { outputChannelsApi } from '../../services/output-channels-api';
import { DynamicAnalysisResult } from '../../components/dynamic-analysis';
import { formatDuration } from '../../components/dynamic-analysis/format';
import { useTimezone } from '../../contexts/TimezoneContext';
import { handleApiError } from '../../services/api';
import { AxiosError } from 'axios';
import toast from 'react-hot-toast';

// ============================================================================
// RESULT CARD COMPONENT
// ============================================================================

interface ResultCardProps {
  result: DynamicEntryResult;
  isExpanded: boolean;
  onToggle: () => void;
}

const ResultCard: React.FC<ResultCardProps> = ({ result, isExpanded, onToggle }) => {
  const { formatRelativeTime, formatDateTime } = useTimezone();
  const [showMetadata, setShowMetadata] = useState(false);
  const [showMetaAnalysis, setShowMetaAnalysis] = useState(false);

  const getFilterLabel = () => {
    const filter = result.entry_info?.filter;
    if (!filter || !filter.filters || filter.filters.length === 0) {
      return 'Baseline (no filter)';
    }
    // Handle single filter
    if (filter.filters.length === 1) {
      const f = filter.filters[0];
      return `${f.field} = "${f.value}"`;
    }
    // Handle multiple filters (tertiary entries)
    const logic = filter.logic || 'AND';
    const conditions = filter.filters.map((f: { field: string; value: string | number }) => `${f.field}="${f.value}"`);
    return conditions.join(` ${logic} `);
  };

  // Calculate highest absolute impact value from estimatedMissingValueInCurrency and estimatedImpactCountNormalized (Tier1-4 only)
  const getHighestAbsoluteImpact = (): { value: number; display: string } | null => {
    const values: number[] = [];
    
    // Add estimatedMissingValueInCurrency if present
    const missingValue = result.result_data?.ghostTransactionAnalysis?.estimatedMissingValueInCurrency;
    if (missingValue != null && typeof missingValue === 'number') {
      values.push(missingValue);
    }
    
    // Add estimatedImpactCountNormalized from Tier1-4 buckets
    const buckets = result.result_data?.aggregatePerformanceAnalysis || [];
    for (const bucket of buckets) {
      // Skip Tier5
      if (bucket.bucket?.includes('Tier5')) continue;
      
      const impactValues = bucket.estimatedImpactValue || [];
      for (const impact of impactValues) {
        if (impact.estimatedImpactCountNormalized != null && typeof impact.estimatedImpactCountNormalized === 'number') {
          values.push(impact.estimatedImpactCountNormalized);
        }
      }
    }
    
    if (values.length === 0) return null;
    
    // Find the value with highest absolute value
    let maxAbsValue = values[0];
    for (const v of values) {
      if (Math.abs(v) > Math.abs(maxAbsValue)) {
        maxAbsValue = v;
      }
    }
    
    return {
      value: maxAbsValue,
      display: maxAbsValue >= 0 ? `+${maxAbsValue.toFixed(1)}` : maxAbsValue.toFixed(1)
    };
  };

  const highestImpact = getHighestAbsoluteImpact();

  return (
    <Card className={`transition-all ${isExpanded ? 'ring-2 ring-info' : ''}`}>
      <div
        className="p-4 cursor-pointer"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isExpanded ? (
              <ChevronDown className="w-5 h-5 text-muted-foreground" />
            ) : (
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            )}
            
            {result.significance_status ? (
              <CheckCircle className="w-5 h-5 text-success" />
            ) : result.error_message ? (
              <XCircle className="w-5 h-5 text-destructive" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-warn" />
            )}
            
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-foreground">
                  {getFilterLabel()}
                </span>
                <Badge 
                  variant={result.significance_status ? 'success' : result.error_message ? 'error' : 'warning'}
                >
                  {result.significance_status ? 'Significant' : result.error_message ? 'Error' : 'Insufficient Data'}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Level: {result.entry_info?.analysisLevel || 'transactions'}
                {result.execution_time_seconds != null && ` • ${Number(result.execution_time_seconds).toFixed(2)}s`}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5 font-mono">
                Result: {result.id} Job - Meta Analysis: {result.analysis_jobs_dynamic_id}{result.run_id && ` Run: ${result.run_id}`}
              </p>
              {result.entry_info?.metaAnalysis?.itemCodeName && (
                <p className="text-xs text-info mt-0.5">
                  Item: {result.entry_info.metaAnalysis.itemCodeName}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4">
            {highestImpact && (
              <div 
                className={`px-2 py-1 rounded text-xs font-semibold ${
                  highestImpact.value > 0 
                    ? 'bg-success/15 text-success' 
                    : highestImpact.value < 0 
                    ? 'bg-negative/15 text-negative'
                    : 'bg-muted text-muted-foreground'
                }`}
                title="Highest absolute impact value"
              >
                {highestImpact.display}
              </div>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                window.open(`/dynamic-analysis/results/${result.id}`, '_blank');
              }}
              className="p-1.5 hover:bg-accent rounded-md transition-colors"
              title="Open in new tab"
            >
              <ExternalLink className="w-4 h-4 text-muted-foreground hover:text-foreground" />
            </button>
            <div className="text-right">
              {result.result_data?.ghostTransactionAnalysis?.analysisTimestamp && (
                <p className="text-xs text-info" title="Analysis timestamp">
                  {formatDateTime(result.result_data.ghostTransactionAnalysis.analysisTimestamp)}
                </p>
              )}
              <span className="text-xs text-muted-foreground">
                {formatRelativeTime(result.created_at)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && result.result_data && (
        <div className="px-4 pb-4 border-t border-border">
          <div className="pt-4">
            {result.significance_status ? (
              <>
                {/* Meta Analysis Context — Collapsible */}
                {result.entry_info?.metaAnalysis && (
                  <div className="mb-6">
                    <button
                      onClick={() => setShowMetaAnalysis(!showMetaAnalysis)}
                      className="flex items-center gap-2 text-sm font-medium text-foreground mb-3 hover:text-foreground transition-colors"
                    >
                      {showMetaAnalysis ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      Meta Analysis Context
                      <span className="text-xs font-normal text-muted-foreground">
                        {result.entry_info.metaAnalysis.dataFrequencyCategory || ''} frequency · {
                          result.entry_info.metaAnalysis.interArrivalTimeDistributionNormalizedMeanLast40Transactions != null
                            ? `~${formatDuration(result.entry_info.metaAnalysis.interArrivalTimeDistributionNormalizedMeanLast40Transactions)} cycle`
                            : ''
                        }
                      </span>
                    </button>
                    {showMetaAnalysis && (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 bg-muted/50 p-4 rounded-lg">
                        <div>
                          <p className="text-xs text-muted-foreground">Data Frequency</p>
                          <p className="text-sm font-medium text-foreground">
                            {result.entry_info.metaAnalysis.dataFrequencyCategory || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Historical Context</p>
                          <p className="text-sm font-medium text-foreground">
                            {result.entry_info.metaAnalysis.historicalContextType?.replace(/_/g, ' ') || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Avg Entries (7d)</p>
                          <p className="text-sm font-medium text-foreground">
                            {result.entry_info.metaAnalysis.dailyAvgEntriesLast7DaysExclNullDays?.toFixed(2) ?? 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Avg Entries (30d)</p>
                          <p className="text-sm font-medium text-foreground">
                            {result.entry_info.metaAnalysis.dailyAvgEntriesLast30DaysExclNullDays?.toFixed(2) ?? 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Null Days (30d)</p>
                          <p className="text-sm font-medium text-foreground">
                            {result.entry_info.metaAnalysis.numberOfNullDaysLast30Days ?? 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Mean IAT (recent)</p>
                          <p className="text-sm font-medium text-foreground">
                            {result.entry_info.metaAnalysis.interArrivalTimeDistributionMeanLastXDays != null
                              ? formatDuration(result.entry_info.metaAnalysis.interArrivalTimeDistributionMeanLastXDays)
                              : 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Mean IAT (40 txn)</p>
                          <p className="text-sm font-medium text-foreground">
                            {result.entry_info.metaAnalysis.interArrivalTimeDistributionNormalizedMeanLast40Transactions != null
                              ? formatDuration(result.entry_info.metaAnalysis.interArrivalTimeDistributionNormalizedMeanLast40Transactions)
                              : 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Std Dev IAT (40 txn)</p>
                          <p className="text-sm font-medium text-foreground">
                            {result.entry_info.metaAnalysis.interArrivalTimeDistributionStdDevNormalizedLast40Transactions != null
                              ? formatDuration(result.entry_info.metaAnalysis.interArrivalTimeDistributionStdDevNormalizedLast40Transactions)
                              : 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Weekly Cyclicality (F)</p>
                          <p className="text-sm font-medium text-foreground">
                            {result.entry_info.metaAnalysis.weeklyCyclicalityMeasureAnovaFStatistic?.toFixed(2) ?? 'N/A'}
                            {result.entry_info.metaAnalysis.weeklyCyclicalityMeasureAnovaFStatistic != null && (
                              <span className="text-xs font-normal text-muted-foreground ml-1">
                                {result.entry_info.metaAnalysis.weeklyCyclicalityMeasureAnovaFStatistic > 3 ? '(cyclic)' : '(none)'}
                              </span>
                            )}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Daily Cyclicality (F)</p>
                          <p className="text-sm font-medium text-foreground">
                            {result.entry_info.metaAnalysis.dailyCyclicalityMeasureAnovaFStatistic?.toFixed(2) ?? 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Avg Source Data Lag (Fixed Placeholder for now)</p>
                          <p className="text-sm font-medium text-foreground">
                            {result.entry_info.metaAnalysis.avgSourceDataLag != null
                              ? formatDuration(result.entry_info.metaAnalysis.avgSourceDataLag)
                              : 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Quantitative Fields</p>
                          <p className="text-sm font-medium text-foreground">
                            {result.entry_info.metaAnalysis.objectiveQuantitativeFields?.join(', ') || 'N/A'}
                          </p>
                        </div>
                        {result.result_data?.ghostTransactionAnalysis?.historicalActivityAnalysis?.finalAssessment?.lastClosedToOpenTransition && (
                          <div>
                            <p className="text-xs text-muted-foreground">Last Opened</p>
                            <p className="text-sm font-medium text-success">
                              {new Date(result.result_data.ghostTransactionAnalysis.historicalActivityAnalysis.finalAssessment.lastClosedToOpenTransition).toLocaleString()}
                            </p>
                          </div>
                        )}
                        {result.result_data?.ghostTransactionAnalysis?.historicalActivityAnalysis?.finalAssessment?.lastOpenToClosedTransition && (
                          <div>
                            <p className="text-xs text-muted-foreground">Last Closed</p>
                            <p className="text-sm font-medium text-warn">
                              {new Date(result.result_data.ghostTransactionAnalysis.historicalActivityAnalysis.finalAssessment.lastOpenToClosedTransition).toLocaleString()}
                            </p>
                          </div>
                        )}
                        {result.entry_info.metaAnalysis.itemCodeName && (
                          <div>
                            <p className="text-xs text-muted-foreground">Item Name</p>
                            <p className="text-sm font-medium text-info">
                              {result.entry_info.metaAnalysis.itemCodeName}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <DynamicAnalysisResult resultData={result.result_data} meta={result.entry_info?.metaAnalysis} />

                    {/* Raw Result Data Section */}
                    {result.result_data && (
                      <div className="mt-6 border-t border-border pt-4">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowMetadata(!showMetadata);
                          }}
                          className={`
                            w-full flex items-center justify-between px-4 py-3 rounded-lg
                            transition-all duration-200
                            ${showMetadata 
                              ? 'bg-muted border border-input' 
                              : 'bg-muted/50 hover:bg-muted border border-border'}
                            cursor-pointer
                          `}
                        >
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-background rounded-lg shadow-sm">
                              <Code className="w-5 h-5 text-muted-foreground" />
                            </div>
                            <div className="text-left">
                              <span className="font-medium text-foreground">
                                Raw Result Data
                              </span>
                              <p className="text-xs text-muted-foreground">
                                Full analysis result JSON
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {showMetadata ? (
                              <ChevronDown className="w-5 h-5 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="w-5 h-5 text-muted-foreground" />
                            )}
                          </div>
                        </button>

                        {showMetadata && (
                          <div className="mt-4 animate-in slide-in-from-top-2 duration-200">
                            <pre className="p-4 code-block rounded-lg text-xs overflow-x-auto max-h-96 overflow-y-auto font-mono">
                              {JSON.stringify(result.result_data, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    )}
              </>
            ) : result.error_message ? (
              <div className="p-4 bg-destructive/15 rounded-lg">
                <p className="text-sm text-destructive">
                  <strong>Error:</strong> {result.error_message}
                </p>
              </div>
            ) : (
              <div className="p-4 bg-warn/15 rounded-lg">
                <p className="text-sm text-warn">
                  <strong>Error:</strong> {result.result_data?.reason || result.result_data?.error || 'Analysis failed.'}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
};

// ============================================================================
// ENTRY CARD COMPONENT
// ============================================================================

interface EntryCardProps {
  entry: DynamicJobEntry;
  isExpanded: boolean;
  onToggle: () => void;
}

const EntryCard: React.FC<EntryCardProps> = ({ entry, isExpanded, onToggle }) => {
  const { formatRelativeTime } = useTimezone();
  const meta = entry.meta_analysis?.metaAnalysisResults;

  const getFilterInfo = () => {
    const config = entry.dynamic_config?.dynamicAnalysisConfig;
    if (!config) return { label: 'Unknown', isBaseline: true, filterCount: 0 };

    const filter = config.analysisFilter;
    if (!filter || !('filters' in filter) || filter.filters.length === 0) {
      return { label: 'No filter (baseline)', isBaseline: true, filterCount: 0 };
    }

    // Handle single filter
    if (filter.filters.length === 1) {
      const firstFilter = filter.filters[0];
      return {
        label: `${firstFilter.field} = "${firstFilter.value}"`,
        field: firstFilter.field,
        value: firstFilter.value,
        isBaseline: false,
        filterCount: 1
      };
    }

    // Handle multiple filters (tertiary entries)
    const logic = filter.logic || 'AND';
    const conditions = filter.filters.map((f: { field: string; value: string | number }) => `${f.field}="${f.value}"`);
    return {
      label: conditions.join(` ${logic} `),
      isBaseline: false,
      filterCount: filter.filters.length
    };
  };

  const filterInfo = getFilterInfo();

  return (
    <Card className={`transition-all ${isExpanded ? 'ring-2 ring-warn' : ''}`}>
      <div
        className="p-4 cursor-pointer"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isExpanded ? (
              <ChevronDown className="w-5 h-5 text-muted-foreground" />
            ) : (
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            )}
            <div>
              <div className="flex items-center gap-2">
                {filterInfo.isBaseline ? (
                  <Badge variant="info">Baseline</Badge>
                ) : filterInfo.filterCount > 1 ? (
                  <Badge variant="warning">
                    <Filter className="w-3 h-3 mr-1" />
                    {filterInfo.filterCount} Filters
                  </Badge>
                ) : (
                  <Badge variant="neutral">
                    <Filter className="w-3 h-3 mr-1" />
                    Filtered
                  </Badge>
                )}
                <span className="font-medium text-foreground">
                  {filterInfo.label}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Level: {entry.dynamic_config?.dynamicAnalysisConfig?.analysisLevel || 'transactions'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <Badge variant={getEntryStatusVariant(entry.status)}>
              {entry.status}
            </Badge>
            <span className="text-xs text-muted-foreground">
              {formatRelativeTime(entry.created_at)}
            </span>
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && meta && (
        <div className="px-4 pb-4 border-t border-border">
          <div className="pt-4">
            <h4 className="text-sm font-medium text-foreground mb-3">
              Meta-Analysis Configuration
            </h4>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="bg-muted rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">Daily Avg (7d)</p>
                <p className="font-semibold text-foreground">
                  {meta.dailyAvgEntriesLast7DaysExclNullDays?.toFixed(2) || 'N/A'}
                </p>
              </div>

              <div className="bg-muted rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">Weekly F-Stat</p>
                <p className="font-semibold text-foreground">
                  {meta.weeklyCyclicalityMeasureAnovaFStatistic?.toFixed(2) || 'N/A'}
                </p>
              </div>

              <div className="bg-muted rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">Daily F-Stat</p>
                <p className="font-semibold text-foreground">
                  {meta.dailyCyclicalityMeasureAnovaFStatistic?.toFixed(2) ?? 'N/A'}
                </p>
              </div>

              <div className="bg-muted rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">Historical Context</p>
                <p className="font-semibold text-foreground text-xs">
                  {meta.historicalContextType === 'same_day_of_the_week' ? 'Same Day of Week' : 'Previous Periods'}
                </p>
              </div>

              <div className="bg-muted rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">Data Frequency</p>
                <p className="font-semibold text-foreground">
                  {meta.dataFrequencyCategory || 'N/A'}
                </p>
              </div>

              <div className="bg-muted rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">Inter-Arrival (40 txns)</p>
                <p className="font-semibold text-foreground">
                  {meta.interArrivalTimeDistributionNormalizedMeanLast40Transactions != null
                    ? formatDuration(meta.interArrivalTimeDistributionNormalizedMeanLast40Transactions)
                    : 'N/A'}
                </p>
              </div>

              <div className="bg-muted rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">Avg Source Data Lag (Fixed Placeholder for now)</p>
                <p className="font-semibold text-foreground">
                  {meta.avgSourceDataLag != null
                    ? formatDuration(meta.avgSourceDataLag)
                    : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================

const JobDetailPage: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { formatRelativeTime } = useTimezone();
  const [expandedEntries, setExpandedEntries] = useState<Set<string>>(new Set());
  const [expandedResults, setExpandedResults] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'entries' | 'results'>('results');
  const [analysisJobIdFilter, setAnalysisJobIdFilter] = useState<string>('');
  const [runIdFilter, setRunIdFilter] = useState<string>('');
  const [debouncedFilter, setDebouncedFilter] = useState<string>('');
  const [debouncedRunIdFilter, setDebouncedRunIdFilter] = useState<string>('');

  // Debounce the filter inputs (500ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilter(analysisJobIdFilter);
    }, 500);
    return () => clearTimeout(timer);
  }, [analysisJobIdFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedRunIdFilter(runIdFilter);
    }, 500);
    return () => clearTimeout(timer);
  }, [runIdFilter]);

  // Fetch job details
  const { data: jobData, isLoading: jobLoading } = useQuery({
    queryKey: ['dynamic-analysis-job', jobId],
    queryFn: () => dynamicAnalysisApi.getJobDetails(jobId!),
    enabled: !!jobId
  });

  // Fetch job entries
  const { data: entriesData, isLoading: entriesLoading } = useQuery({
    queryKey: ['dynamic-analysis-entries', jobId],
    queryFn: () => dynamicAnalysisApi.getJobEntries(jobId!),
    enabled: !!jobId
  });

  // Fetch job results (with server-side filtering)
  const { data: resultsData, isLoading: resultsLoading, refetch: refetchResults } = useQuery({
    queryKey: ['dynamic-analysis-results', jobId, debouncedFilter, debouncedRunIdFilter],
    queryFn: () => dynamicAnalysisApi.getJobResults(jobId!, 100, debouncedFilter || undefined, debouncedRunIdFilter || undefined),
    enabled: !!jobId,
    refetchInterval: 30000, // Refetch every 30 seconds
    retry: (failureCount, error) => {
      // Don't retry on rate limit errors
      if (error instanceof AxiosError && error.response?.status === 429) return false;
      return failureCount < 1;
    }
  });

  // Fetch execution job (Phase 2)
  const { data: executionJobData } = useQuery({
    queryKey: ['dynamic-analysis-execution-job', jobId],
    queryFn: () => dynamicAnalysisApi.getExecutionJobByParent(jobId!),
    enabled: !!jobId
  });

  // Fetch output channels list (for resolving the configured channel label)
  const { data: outputChannelsData } = useQuery({
    queryKey: ['output-channels'],
    queryFn: () => outputChannelsApi.getOutputChannels(),
  });

  // Toggle status mutation
  const toggleStatusMutation = useMutation({
    mutationFn: async ({ enable }: { enable: boolean }) => {
      return enable
        ? dynamicAnalysisApi.enableJob(jobId!)
        : dynamicAnalysisApi.disableJob(jobId!);
    },
    onSuccess: (_, { enable }) => {
      toast.success(`Job ${enable ? 'enabled' : 'disabled'} successfully`);
      queryClient.invalidateQueries({ queryKey: ['dynamic-analysis-job', jobId] });
    },
    onError: (error) => {
      toast.error(handleApiError(error) || 'Failed to update job status');
    }
  });

  // Run job mutation (regenerate entries)
  const runJobMutation = useMutation({
    mutationFn: () => dynamicAnalysisApi.runJob(jobId!),
    onSuccess: () => {
      toast.success('Entries regenerated successfully');
      queryClient.invalidateQueries({ queryKey: ['dynamic-analysis-job', jobId] });
      queryClient.invalidateQueries({ queryKey: ['dynamic-analysis-entries', jobId] });
    },
    onError: (error) => {
      toast.error(handleApiError(error) || 'Failed to regenerate entries');
    }
  });

  // Execute analysis mutation
  const executeAnalysisMutation = useMutation({
    mutationFn: () => dynamicAnalysisApi.executeJobAnalysis(jobId!),
    onSuccess: (data) => {
      const result = data.result;
      toast.success(
        `Analysis complete: ${result.progress.successCount} processed, ${result.progress.errorCount} errors`
      );
      queryClient.invalidateQueries({ queryKey: ['dynamic-analysis-results', jobId] });
    },
    onError: (error) => {
      toast.error(handleApiError(error) || 'Failed to execute analysis');
    }
  });

  const job = jobData?.job;
  const entries = entriesData?.entries || [];
  const results = resultsData?.results || [];
  const isActive = job?.enabled && job?.status === 'active';

  const configuredOutputChannel = jobData?.outputChannel ?? null;
  const outputChannelLabel = (() => {
    if (!configuredOutputChannel) return 'In-app only';
    const match = outputChannelsData?.channels.find(
      (ch) => ch.source === configuredOutputChannel.source && ch.id === configuredOutputChannel.id,
    );
    return match ? `${match.label} (${match.type.toUpperCase()})` : 'Configured';
  })();

  const toggleEntry = (entryId: string) => {
    setExpandedEntries(prev => {
      const next = new Set(prev);
      if (next.has(entryId)) {
        next.delete(entryId);
      } else {
        next.add(entryId);
      }
      return next;
    });
  };

  const toggleResult = (resultId: string) => {
    setExpandedResults(prev => {
      const next = new Set(prev);
      if (next.has(resultId)) {
        next.delete(resultId);
      } else {
        next.add(resultId);
      }
      return next;
    });
  };

  const handleToggleStatus = () => {
    toggleStatusMutation.mutate({ enable: !isActive });
  };

  const handleRegenerateEntries = () => {
    if (window.confirm('This will regenerate all dynamic analysis entries (meta-analysis configurations). Continue?')) {
      runJobMutation.mutate();
    }
  };

  const handleExecuteAnalysis = () => {
    if (entries.length === 0) {
      toast.error('No entries to analyze. Generate entries first.');
      return;
    }
    executeAnalysisMutation.mutate();
  };

  if (jobLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Job not found</p>
        <Button onClick={() => navigate('/dynamic-analysis')} className="mt-4">
          Back to Jobs
        </Button>
      </div>
    );
  }

  // Group entries by status
  const enabledEntries = entries.filter(e => e.status === 'enabled');
  const successfulResults = results.filter(r => r.significance_status);
  const errorResults = results.filter(r => r.error_message);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/dynamic-analysis')}
        >
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <Zap className="w-6 h-6 text-warn" />
            <h1 className="text-2xl font-bold text-foreground">
              {job.name}
            </h1>
            <Badge variant={isActive ? 'success' : 'neutral'}>
              {isActive ? 'Active' : 'Inactive'}
            </Badge>
          </div>
          {(job.description || job.config?.analysisDescription) && (
            <p className="text-muted-foreground mt-1">
              {job.description || job.config?.analysisDescription}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={isActive ? 'outline' : 'primary'}
            onClick={handleToggleStatus}
            disabled={toggleStatusMutation.isPending}
          >
            {isActive ? (
              <>
                <Square className="w-4 h-4 mr-2" />
                Disable
              </>
            ) : (
              <>
                <Play className="w-4 h-4 mr-2" />
                Enable
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Job Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-warn/15 rounded-lg">
              <Layers className="w-5 h-5 text-warn" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Entries</p>
              <p className="text-2xl font-bold text-foreground">{enabledEntries.length}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-success/15 rounded-lg">
              <CheckCircle className="w-5 h-5 text-success" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Processed</p>
              <p className="text-2xl font-bold text-foreground">{successfulResults.length}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-destructive/15 rounded-lg">
              <XCircle className="w-5 h-5 text-destructive" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Errors</p>
              <p className="text-2xl font-bold text-foreground">{errorResults.length}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-info/15 rounded-lg">
              <Clock className="w-5 h-5 text-info" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Schedule</p>
              <p className="text-lg font-bold text-foreground">
                {getScheduleLabel(job.schedule)}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-accent rounded-lg">
              <Send className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Output Channel</p>
              <p className="text-lg font-bold text-foreground truncate">
                {outputChannelLabel}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-accent rounded-lg">
              <BarChart2 className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Last Run</p>
              <p className="text-lg font-bold text-foreground">
                {job.lastRunAt ? formatRelativeTime(job.lastRunAt) : 'Never'}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Action Buttons */}
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-foreground">Analysis Actions</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Regenerate entries or execute statistical analysis
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={handleRegenerateEntries}
              disabled={runJobMutation.isPending}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${runJobMutation.isPending ? 'animate-spin' : ''}`} />
              Regenerate Entries
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(`/dynamic-analysis/jobs/${jobId}/execution`)}
            >
              <Settings className="w-4 h-4 mr-2" />
              {executionJobData?.exists ? 'Edit Scheduled Analysis' : 'Configure Scheduled Analysis'}
            </Button>
            <Button
              onClick={handleExecuteAnalysis}
              disabled={executeAnalysisMutation.isPending || entries.length === 0}
              className="bg-primary hover:bg-primary/90"
            >
              {executeAnalysisMutation.isPending ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Running Analysis...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Run Analysis
                </>
              )}
            </Button>
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <div className="border-b border-border">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('results')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'results'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Analysis Results ({results.length}{debouncedFilter ? ' filtered' : ''})
          </button>
          <button
            onClick={() => setActiveTab('entries')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'entries'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Dynamic Entries ({entries.length})
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'results' ? (
        <Card>
          <div className="p-6 border-b border-border">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-foreground">
                  Analysis Results
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Recent statistical analysis results for all entries
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Filter by Job ID..."
                    value={analysisJobIdFilter}
                    onChange={(e) => setAnalysisJobIdFilter(e.target.value)}
                    className="w-48 px-3 py-1.5 text-sm border border-input rounded-md bg-card text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-ring focus:border-primary"
                  />
                  {analysisJobIdFilter && (
                    <button
                      onClick={() => setAnalysisJobIdFilter('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Filter by Run ID..."
                    value={runIdFilter}
                    onChange={(e) => setRunIdFilter(e.target.value)}
                    className="w-48 px-3 py-1.5 text-sm border border-input rounded-md bg-card text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-ring focus:border-primary"
                  />
                  {runIdFilter && (
                    <button
                      onClick={() => setRunIdFilter('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <Button variant="outline" size="sm" onClick={() => refetchResults()}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </div>
          </div>

          <div className="p-6">
            {resultsLoading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner size="lg" />
              </div>
            ) : results.length === 0 && !debouncedFilter && !debouncedRunIdFilter ? (
              <div className="text-center py-8">
                <BarChart2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  No analysis results yet. Click "Run Analysis" to execute statistical analysis.
                </p>
              </div>
            ) : results.length === 0 && (debouncedFilter || debouncedRunIdFilter) ? (
              <div className="text-center py-8">
                <Filter className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  No results match the filter{debouncedFilter ? ` Job ID: "${debouncedFilter}"` : ''}{debouncedRunIdFilter ? ` Run ID: "${debouncedRunIdFilter}"` : ''}
                </p>
                <Button variant="outline" size="sm" onClick={() => setAnalysisJobIdFilter('')} className="mt-4">
                  Clear Filter
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {results.map((result) => (
                  <ResultCard
                    key={result.id}
                    result={result}
                    isExpanded={expandedResults.has(result.id)}
                    onToggle={() => toggleResult(result.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </Card>
      ) : (
        <Card>
          <div className="p-6 border-b border-border">
            <h2 className="text-lg font-semibold text-foreground">
              Dynamic Job Entries
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Auto-generated analysis configurations (meta-analysis)
            </p>
          </div>

          <div className="p-6">
            {entriesLoading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner size="lg" />
              </div>
            ) : entries.length === 0 ? (
              <div className="text-center py-8">
                <Layers className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  No entries generated yet. Click "Regenerate Entries" to generate configurations.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {enabledEntries.map((entry) => (
                  <EntryCard
                    key={entry.id}
                    entry={entry}
                    isExpanded={expandedEntries.has(entry.id)}
                    onToggle={() => toggleEntry(entry.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
};

export default JobDetailPage;
